var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { requireAnyOrgPermission, requireAnySystemPermission } from '../../../../../../backend/features/permissions/shared';
import { useContactManagementTranslation, useUserState } from '../../../../../../ui/hooks';
import { DataTree } from '@juvare/common-data-table-ui/DataTree';
import { FiltersBar, useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { isEmptyFilter } from '@juvare/common-data-table-ui/FiltersBar/utils';
import { usePropChange } from '@juvare/common-data-table-ui/hooks';
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { usePlatformLoader } from '@juvare/platform-sdk-react/ui/hooks';
import AddIcon from '@mui/icons-material/Add';
import { Box, FormControlLabel, Switch } from '@mui/material';
import React, { useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useOrganizationsTree } from '../../../hooks/useOrganizationsTree';
import { CustomOrganizationTreeItem } from './CustomOrganizationTreeItem';
import { OrganizationSlideout } from './OrganizationSlideout';
import { OrganizationsPageHeader } from './OrganizationsPageHeader';
export const OrganizationsTree = (_a) => {
    var { onChangeTreeRenderKey } = _a, props = __rest(_a, ["onChangeTreeRenderKey"]);
    const { t } = useContactManagementTranslation();
    const userState = useUserState();
    const { onStatelessReload } = usePlatformLoader({});
    const navigationContext = useContext(NavigationContext);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const allowUserManageOrganizationMembers = requireAnySystemPermission(userState, 'manage-organizations', 'all-organizations-admin') ||
        requireAnyOrgPermission(userState, 'manage-members');
    const showPendingOnly = searchParams.get('showPendingOnly') === 'true';
    const staticFilters = showPendingOnly
        ? {
            pending: true,
        }
        : undefined;
    const { dataTreeProps, filtersBarProps, totalPendingMemberships, appliedFilterData, activeOrganizationId, reload } = useOrganizationsTree({
        staticFilters,
    });
    usePropChange(appliedFilterData, (newFilterData) => {
        if (onChangeTreeRenderKey && isEmptyFilter(newFilterData)) {
            onChangeTreeRenderKey();
        }
    });
    const mainAction = {
        label: t('panels.organizations.addOrg'),
        icon: React.createElement(AddIcon, null),
        onClick: () => {
            navigationContext.navigate(() => {
                navigate({ pathname: 'new/' });
            });
        },
        'data-test': 'add-organization-button',
    };
    const filterConfig = useFilterConfig((builder) => {
        builder.quickSearchFilter({
            sx: { maxWidth: { xs: undefined, sm: 340 } },
            label: t('common.search'),
            dataPath: 'search',
            dataTest: 'search-filter',
        });
        builder.quickFilterSelect({
            widget: 'form.select',
            sx: { maxWidth: { xs: undefined, sm: 280 } },
            label: t('panels.organizations.permission'),
            dataPath: 'permission',
            values: [
                { label: t('panels.organizations.manageFilter'), value: 'manage' },
                { label: t('panels.organizations.memberOfFilter'), value: 'member' },
            ],
            dataTest: 'permission-filter',
        });
    }, [t]);
    return (React.createElement(React.Fragment, null,
        !props.hideHeader && React.createElement(OrganizationsPageHeader, { pendingMemberships: totalPendingMemberships.toString() }),
        React.createElement(Box, { sx: { p: 4 } },
            React.createElement(DataTree, Object.assign({}, dataTreeProps, { border: true, mainAction: requireAnySystemPermission(userState, 'manage-organizations') ? mainAction : undefined, filtersBar: React.createElement(FiltersBar, Object.assign({}, filtersBarProps, { config: filterConfig })), customHeaderElement: allowUserManageOrganizationMembers && (React.createElement(FormControlLabel, { sx: { mt: -2, mb: -2 }, label: t('panels.organizations.pendingOnly'), control: React.createElement(Switch, { checked: showPendingOnly, onChange: (_event, checked) => {
                            const newSearchParams = new URLSearchParams(searchParams);
                            if (checked) {
                                newSearchParams.set('showPendingOnly', 'true');
                            }
                            else {
                                newSearchParams.delete('showPendingOnly');
                            }
                            setSearchParams(newSearchParams, { replace: true });
                            if (onChangeTreeRenderKey) {
                                onChangeTreeRenderKey();
                            }
                            reload();
                        } }) })), itemContentComponent: (props) => {
                    var _a;
                    return (React.createElement(CustomOrganizationTreeItem, Object.assign({}, props, { membershipCount: (_a = props.item.pendingMemberCount) !== null && _a !== void 0 ? _a : 0, onReload: () => {
                            if (onChangeTreeRenderKey) {
                                onChangeTreeRenderKey();
                            }
                        } })));
                } }))),
        React.createElement(OrganizationSlideout, { organizationId: activeOrganizationId, onClose: (reloadOrgs) => {
                dataTreeProps.onChangeActiveItemId(undefined);
                onStatelessReload()
                    .then(() => {
                    if (reloadOrgs && onChangeTreeRenderKey) {
                        onChangeTreeRenderKey();
                    }
                })
                    .catch(handleErrorDialog);
            } })));
};
